import { useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useLang } from "../../contexts/LanguageContext";
import { useCookieConsent } from "../../contexts/CookieConsentContext";

/**
 * GDPR cookie consent banner. Renders only when the user has not made an
 * explicit choice yet OR when they click "Preferencias de cookies" in the
 * footer. Fixed to the bottom of the viewport, not modal (WCAG-friendly).
 */
export default function CookieBanner() {
  const { t } = useLang();
  const { bannerOpen, acceptAll, rejectAll, savePrefs, consent } = useCookieConsent();
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({
    analytics: consent.analytics,
    marketing: consent.marketing,
    functional: consent.functional,
  });

  if (!bannerOpen) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] p-4 md:p-6"
      role="dialog"
      aria-label={t("cookies.title")}
      data-testid="cookie-banner"
    >
      <div className="max-w-6xl mx-auto bg-white border border-gray-200 shadow-2xl">
        <div className="p-5 md:p-6">
          <div className="flex gap-4 items-start">
            <div className="hidden md:flex w-11 h-11 shrink-0 bg-[#0F2040] text-cecit-gold items-center justify-center">
              <Cookie className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-serif text-xl md:text-2xl text-[#0F2040] mb-1.5">{t("cookies.title")}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {t("cookies.body")}{" "}
                <Link
                  to="/politica-cookies"
                  className="text-[#C8102E] hover:underline"
                  data-testid="cookie-policy-link"
                >
                  {t("cookies.policyLink")}
                </Link>
              </p>

              {expanded && (
                <div className="mt-5 space-y-3 border-t border-gray-100 pt-4" data-testid="cookie-prefs">
                  <PrefRow
                    label={t("cookies.catNecessary")}
                    desc={t("cookies.catNecessaryDesc")}
                    checked
                    disabled
                    lockedLabel={t("cookies.alwaysOn")}
                  />
                  <PrefRow
                    label={t("cookies.catAnalytics")}
                    desc={t("cookies.catAnalyticsDesc")}
                    checked={prefs.analytics}
                    onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                    testid="cookie-pref-analytics"
                  />
                  <PrefRow
                    label={t("cookies.catFunctional")}
                    desc={t("cookies.catFunctionalDesc")}
                    checked={prefs.functional}
                    onChange={(v) => setPrefs((p) => ({ ...p, functional: v }))}
                    testid="cookie-pref-functional"
                  />
                  <PrefRow
                    label={t("cookies.catMarketing")}
                    desc={t("cookies.catMarketingDesc")}
                    checked={prefs.marketing}
                    onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                    testid="cookie-pref-marketing"
                  />
                </div>
              )}

              <div className="mt-5 flex flex-wrap gap-2">
                {!expanded ? (
                  <>
                    <button
                      onClick={acceptAll}
                      data-testid="cookie-accept-all"
                      className="bg-[#C8102E] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#A30D25]"
                    >
                      {t("cookies.acceptAll")}
                    </button>
                    <button
                      onClick={rejectAll}
                      data-testid="cookie-reject-all"
                      className="border border-gray-300 text-[#0F2040] px-5 py-2.5 text-sm font-medium hover:border-[#0F2040]"
                    >
                      {t("cookies.rejectAll")}
                    </button>
                    <button
                      onClick={() => setExpanded(true)}
                      data-testid="cookie-customize"
                      className="text-[#0F2040] px-3 py-2.5 text-sm font-medium hover:underline"
                    >
                      {t("cookies.customize")}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => savePrefs(prefs)}
                      data-testid="cookie-save-prefs"
                      className="bg-[#0F2040] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#1A2F5C]"
                    >
                      {t("cookies.savePrefs")}
                    </button>
                    <button
                      onClick={acceptAll}
                      data-testid="cookie-accept-all"
                      className="border border-gray-300 text-[#0F2040] px-5 py-2.5 text-sm font-medium hover:border-[#0F2040]"
                    >
                      {t("cookies.acceptAll")}
                    </button>
                    <button
                      onClick={rejectAll}
                      data-testid="cookie-reject-all"
                      className="text-[#0F2040] px-3 py-2.5 text-sm font-medium hover:underline"
                    >
                      {t("cookies.rejectAll")}
                    </button>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={rejectAll}
              className="text-gray-400 hover:text-gray-700 -mt-1 -mr-1 p-1"
              aria-label="Close"
              data-testid="cookie-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrefRow({ label, desc, checked, disabled, onChange, testid, lockedLabel }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#0F2040]">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{desc}</p>
      </div>
      {disabled ? (
        <span className="text-[11px] uppercase tracking-wider text-gray-400 mt-1">{lockedLabel}</span>
      ) : (
        <label className="inline-flex items-center cursor-pointer mt-1">
          <input
            type="checkbox"
            checked={!!checked}
            onChange={(e) => onChange?.(e.target.checked)}
            className="sr-only peer"
            data-testid={testid}
          />
          <span className="relative w-10 h-6 bg-gray-200 peer-checked:bg-[#C8102E] transition-colors">
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white shadow transition-transform ${
                checked ? "translate-x-4" : ""
              }`}
            />
          </span>
        </label>
      )}
    </div>
  );
}
